import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Globe, Sparkles, ArrowRight, ChevronRight, ExternalLink, Settings,
  Eye, MousePointerClick, MessageCircle, Percent, Search, Code, BarChart3,
  TrendingUp, TrendingDown, Minus, Car
} from 'lucide-react';
import { TAGLINES } from '../constants/dashboard';
import { dashboardApi } from '../services/dashboardApi';
import api from '../services/api';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------
const Section = ({ children, className = '' }) => (
  <section className={`mb-16 ${className}`}>{children}</section>
);

const SectionTitle = ({ children, sub }) => (
  <div className="text-center mb-10">
    <h2 className="text-2xl md:text-3xl font-bold text-primary mb-3">{children}</h2>
    {sub && <p className="text-secondary max-w-xl mx-auto">{sub}</p>}
  </div>
);

const TrendBadge = ({ current, previous }) => {
  if (previous === 0 && current === 0) return null;

  let pct, TrendIcon, color, bg;
  if (previous === 0) {
    pct = 100;
    TrendIcon = TrendingUp;
    color = 'text-green-400';
    bg = 'bg-green-500/10';
  } else {
    pct = Math.round(((current - previous) / previous) * 100);
    if (pct > 0) {
      TrendIcon = TrendingUp;
      color = 'text-green-400';
      bg = 'bg-green-500/10';
    } else if (pct < 0) {
      TrendIcon = TrendingDown;
      color = 'text-red-400';
      bg = 'bg-red-500/10';
    } else {
      TrendIcon = Minus;
      color = 'text-secondary';
      bg = 'bg-gray-500/10';
    }
  }

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${color} ${bg}`}>
      <TrendIcon className="h-3 w-3" />
      {pct > 0 ? '+' : ''}{pct}%
    </span>
  );
};

// ---------------------------------------------------------------------------
// Onboarding data
// ---------------------------------------------------------------------------
const PILLARS = [
  {
    icon: Globe,
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/20',
    title: 'Catalogue en ligne',
    desc: 'Affichez tous vos véhicules sur une page personnalisée, accessible depuis n\'importe quel appareil.',
  },
  {
    icon: Search,
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/20',
    title: 'Visibilité IA & SEO',
    desc: 'Votre vitrine est optimisée pour Google et les moteurs de recherche IA comme ChatGPT, Perplexity et Copilot.',
  },
  {
    icon: Code,
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/20',
    title: 'Widget intégrable',
    desc: 'Intégrez votre catalogue directement sur votre site existant grâce à un simple widget JavaScript.',
  },
];

const STEPS = [
  { num: 1, title: 'Choisissez votre slug', desc: 'Définissez l\'URL unique de votre vitrine en ligne.' },
  { num: 2, title: 'Personnalisez', desc: 'Ajoutez votre logo, couleurs et informations de contact.' },
  { num: 3, title: 'Intégrez le widget', desc: 'Copiez le code et ajoutez-le à votre site web existant.' },
  { num: 4, title: 'Suivez vos stats', desc: 'Analysez les vues, clics et contacts générés par votre vitrine.' },
];

// ---------------------------------------------------------------------------
// Onboarding component
// ---------------------------------------------------------------------------
const WebsiteOnboarding = () => {
  const tagline = TAGLINES.vitrine.fr;

  return (
    <div className="max-w-5xl mx-auto pb-16">
      {/* HERO */}
      <Section>
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-cyan-500/[0.08] via-dark-card to-dark-surface border border-cyan-500/20 p-8 md:p-12">
          <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-cyan-500/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />

          <div className="relative z-10 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6">
              <Sparkles className="h-3.5 w-3.5 text-cyan-400" />
              <span className="text-xs font-medium text-cyan-300">Vitrine Digitale</span>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4 leading-tight">
              {tagline.slogan}
            </h1>

            <p className="text-lg text-secondary mb-8 leading-relaxed">
              {tagline.description}
            </p>

            <Link
              to="/settings?tab=vitrine"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white font-semibold transition-colors"
            >
              Configurer ma vitrine
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </Section>

      {/* 3 PILLARS */}
      <Section>
        <SectionTitle sub="Tout ce dont vous avez besoin pour être visible en ligne.">
          Pourquoi créer votre vitrine ?
        </SectionTitle>

        <div className="grid md:grid-cols-3 gap-5">
          {PILLARS.map((p) => (
            <div
              key={p.title}
              className={`rounded-xl border ${p.border} ${p.bg} p-6 transition-all hover:scale-[1.02] hover:shadow-lg`}
            >
              <div className={`w-10 h-10 rounded-lg ${p.bg} flex items-center justify-center mb-4`}>
                <p.icon className={`h-5 w-5 ${p.color}`} />
              </div>
              <h3 className="text-lg font-semibold text-primary mb-2">{p.title}</h3>
              <p className="text-sm text-secondary leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section>
        <SectionTitle sub="De la configuration au premier contact, en quelques minutes.">
          Comment ça marche
        </SectionTitle>

        <div className="relative">
          <div className="hidden md:block absolute top-8 left-0 right-0 h-0.5 bg-dark-border" style={{ left: '12.5%', right: '12.5%' }} />

          <div className="grid md:grid-cols-4 gap-6">
            {STEPS.map((s, idx) => (
              <div key={s.num} className="relative text-center">
                <div className="relative z-10 w-16 h-16 rounded-full bg-dark-card border-2 border-cyan-500/40 flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-cyan-400">{s.num}</span>
                </div>
                <h4 className="text-sm font-semibold text-primary mb-1.5">{s.title}</h4>
                <p className="text-xs text-muted leading-relaxed">{s.desc}</p>
                {idx < STEPS.length - 1 && (
                  <ChevronRight className="md:hidden h-5 w-5 text-dark-border mx-auto mt-4" />
                )}
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section className="mb-0">
        <div className="rounded-xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/[0.06] to-dark-card p-8 md:p-10 text-center">
          <Globe className="h-12 w-12 text-cyan-500/60 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-primary mb-2">Prêt à lancer votre vitrine ?</h3>
          <p className="text-secondary mb-6 max-w-md mx-auto">
            Configurez votre vitrine en quelques clics et rendez vos véhicules visibles partout sur le web.
          </p>
          <Link
            to="/settings?tab=vitrine"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white font-semibold transition-colors"
          >
            <Settings className="h-4 w-4" />
            Configurer ma vitrine
          </Link>
        </div>
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Dashboard component
// ---------------------------------------------------------------------------
const KpiCard = ({ icon: Icon, label, value, sub, color = 'text-cyan-400' }) => (
  <div className="bg-dark-card rounded-xl border border-dark-border p-4 flex flex-col">
    <div className="flex items-center gap-2 mb-3">
      <div className={`w-8 h-8 rounded-lg bg-dark-hover flex items-center justify-center`}>
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
      <span className="text-xs text-muted font-medium">{label}</span>
    </div>
    <span className="text-2xl font-bold text-primary">{value}</span>
    {sub && <span className="text-[11px] text-muted mt-1">{sub}</span>}
  </div>
);

const WebsiteDashboard = ({ settings }) => {
  const [analytics, setAnalytics] = useState(null);
  const [weeklyTrend, setWeeklyTrend] = useState(null);
  const [loading, setLoading] = useState(true);

  const vitrineUrl = settings?.vitrine_url;

  useEffect(() => {
    Promise.all([
      dashboardApi.getVitrineAnalytics(30),
      dashboardApi.getVitrineAnalytics(7),
      dashboardApi.getVitrineAnalytics(14),
    ]).then(([full, week7, week14]) => {
      setAnalytics(full);
      if (week7?.totals && week14?.totals) {
        setWeeklyTrend({
          thisWeek: week7.totals,
          lastWeek: {
            widget_loads: week14.totals.widget_loads - week7.totals.widget_loads,
            vehicle_clicks: week14.totals.vehicle_clicks - week7.totals.vehicle_clicks,
            contact_clicks: week14.totals.contact_clicks - week7.totals.contact_clicks,
          },
        });
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const totals = analytics?.totals || { widget_loads: 0, vehicle_clicks: 0, contact_clicks: 0 };
  const ctr = totals.widget_loads > 0
    ? ((totals.vehicle_clicks / totals.widget_loads) * 100).toFixed(1)
    : '0.0';
  const hasTraffic = totals.widget_loads > 0;

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto pb-16">
        <div className="flex items-center justify-center py-20">
          <div className="h-6 w-6 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto pb-16 space-y-6">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
            <Globe className="h-5 w-5 text-cyan-500" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-primary">Ma vitrine digitale</h1>
            <p className="text-sm text-muted">Statistiques des 30 derniers jours</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {vitrineUrl && (
            <a
              href={vitrineUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-cyan-500/10 text-cyan-400 rounded-lg hover:bg-cyan-500/20 transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Voir le site
            </a>
          )}
          <Link
            to="/settings?tab=vitrine"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-dark-border text-secondary rounded-lg hover:bg-dark-hover transition-colors"
          >
            <Settings className="h-3.5 w-3.5" />
            Configurer
          </Link>
        </div>
      </div>

      {!hasTraffic ? (
        /* EMPTY STATE - no traffic yet */
        <div className="rounded-xl border border-dashed border-cyan-500/20 bg-gradient-to-br from-cyan-500/[0.04] to-dark-card p-10 text-center">
          <BarChart3 className="h-12 w-12 text-cyan-500/40 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-primary mb-2">Aucun trafic pour le moment</h3>
          <p className="text-sm text-secondary mb-6 max-w-md mx-auto">
            Votre vitrine est configurée et prête à recevoir des visiteurs.
            Partagez votre lien ou intégrez le widget sur votre site pour commencer à générer du trafic.
          </p>
          <div className="flex items-center justify-center gap-3">
            {vitrineUrl && (
              <a
                href={vitrineUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Voir ma vitrine
              </a>
            )}
            <Link
              to="/settings?tab=vitrine"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border border-dark-border text-secondary rounded-lg hover:bg-dark-hover transition-colors"
            >
              <Code className="h-3.5 w-3.5" />
              Obtenir le widget
            </Link>
          </div>
        </div>
      ) : (
        <>
          {/* 4 KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard
              icon={Eye}
              label="Vues widget"
              value={totals.widget_loads.toLocaleString('fr-FR')}
              sub="30 derniers jours"
            />
            <KpiCard
              icon={MousePointerClick}
              label="Clics véhicules"
              value={totals.vehicle_clicks.toLocaleString('fr-FR')}
              color="text-blue-400"
            />
            <KpiCard
              icon={MessageCircle}
              label="Contacts"
              value={totals.contact_clicks.toLocaleString('fr-FR')}
              color="text-green-400"
            />
            <KpiCard
              icon={Percent}
              label="Taux de clic (CTR)"
              value={`${ctr}%`}
              sub={totals.vehicle_clicks > 0
                ? `Contact : ${((totals.contact_clicks / totals.vehicle_clicks) * 100).toFixed(1)}%`
                : null}
              color="text-amber-400"
            />
          </div>

          {/* WEEKLY TREND */}
          {weeklyTrend && (
            <div className="bg-dark-card rounded-xl border border-dark-border p-5">
              <h3 className="text-sm font-semibold text-primary mb-4">Tendance hebdomadaire</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center justify-between p-3 bg-dark-surface rounded-lg border border-dark-border">
                  <div>
                    <p className="text-xs text-muted mb-1">Vues</p>
                    <p className="text-lg font-bold text-primary">{weeklyTrend.thisWeek.widget_loads}</p>
                  </div>
                  <TrendBadge current={weeklyTrend.thisWeek.widget_loads} previous={weeklyTrend.lastWeek.widget_loads} />
                </div>
                <div className="flex items-center justify-between p-3 bg-dark-surface rounded-lg border border-dark-border">
                  <div>
                    <p className="text-xs text-muted mb-1">Clics</p>
                    <p className="text-lg font-bold text-primary">{weeklyTrend.thisWeek.vehicle_clicks}</p>
                  </div>
                  <TrendBadge current={weeklyTrend.thisWeek.vehicle_clicks} previous={weeklyTrend.lastWeek.vehicle_clicks} />
                </div>
                <div className="flex items-center justify-between p-3 bg-dark-surface rounded-lg border border-dark-border">
                  <div>
                    <p className="text-xs text-muted mb-1">Contacts</p>
                    <p className="text-lg font-bold text-primary">{weeklyTrend.thisWeek.contact_clicks}</p>
                  </div>
                  <TrendBadge current={weeklyTrend.thisWeek.contact_clicks} previous={weeklyTrend.lastWeek.contact_clicks} />
                </div>
              </div>
            </div>
          )}

          {/* 2 COLUMNS: Sources + Top vehicles */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Traffic sources */}
            <div className="bg-dark-card rounded-xl border border-dark-border p-5">
              <h3 className="text-sm font-semibold text-primary mb-4">Sources de trafic</h3>
              {analytics?.sources?.length > 0 ? (
                <div className="space-y-2.5">
                  {analytics.sources.slice(0, 8).map(s => {
                    const total = analytics.sources.reduce((sum, x) => sum + x.count, 0);
                    const pct = total > 0 ? (s.count / total) * 100 : 0;
                    const isAI = ['ChatGPT', 'Copilot', 'Gemini', 'Claude', 'Perplexity', 'You.com'].includes(s.source);
                    return (
                      <div key={s.source} className="flex items-center gap-3">
                        <span className={`text-sm font-medium w-28 truncate ${isAI ? 'text-purple-400' : 'text-label'}`}>
                          {isAI && '✦ '}{s.source}
                        </span>
                        <div className="flex-1 h-2 bg-dark-surface rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${isAI ? 'bg-purple-500' : 'bg-cyan-500/60'}`}
                            style={{ width: `${Math.max(pct, 3)}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted w-10 text-right">{s.count}</span>
                        <span className="text-[10px] text-muted w-10 text-right">{pct.toFixed(0)}%</span>
                      </div>
                    );
                  })}
                  {analytics.sources.some(s => ['ChatGPT', 'Copilot', 'Gemini', 'Claude', 'Perplexity', 'You.com'].includes(s.source)) && (
                    <p className="text-[10px] text-purple-400/70 mt-2">✦ Source IA détectée</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted">Aucune source de trafic détectée</p>
              )}
            </div>

            {/* Top vehicles */}
            <div className="bg-dark-card rounded-xl border border-dark-border p-5">
              <h3 className="text-sm font-semibold text-primary mb-4">Top véhicules cliqués</h3>
              {analytics?.top_vehicles?.length > 0 ? (
                <div className="space-y-2">
                  {analytics.top_vehicles.slice(0, 8).map((v, i) => {
                    const label = v.make && v.model
                      ? `${v.make} ${v.model}`
                      : v.make || `Véhicule #${v.vehicle_id?.slice(-6)}`;
                    return (
                      <Link
                        key={v.vehicle_id || i}
                        to={`/vehicles/${v.vehicle_id}`}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-dark-hover transition-colors group"
                      >
                        <span className="text-xs font-bold text-muted w-5 text-center">{i + 1}</span>
                        <Car className="h-4 w-4 text-cyan-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-primary truncate group-hover:text-cyan-400 transition-colors">{label}</p>
                        </div>
                        <span className="text-xs text-muted">{v.clicks || v.count} clics</span>
                        <ChevronRight className="h-3.5 w-3.5 text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted">Aucun clic sur les véhicules pour le moment</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Website page
// ---------------------------------------------------------------------------
const Website = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/settings')
      .then(res => setSettings(res.data))
      .catch(() => setSettings(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin" />
      </div>
    );
  }

  const isConfigured = settings?.vitrine_enabled && settings?.dealer_slug;

  return isConfigured
    ? <WebsiteDashboard settings={settings} />
    : <WebsiteOnboarding />;
};

export default Website;
